import { useEffect, useMemo, useRef } from 'react';
import dayjs from 'dayjs';
import { generatePath, useMatch, useNavigate } from 'react-router-dom';
import { useNavReconPipelineMetadata } from '../../../../api/rs-hg/nav-recon/hooks/queries';
import {
  hasUserResource,
  NAV_RECONCILIATION_RESOURCE_NAME,
  useUserResources,
} from '../../../../api/rs3-public/rsAuthAuthorization';
import { OverviewsRoutePaths } from '../../../../routes/page-routes';
import TokenService from '../../../../services/token-service';
import { toast } from '../../../toast/Toast';
import { getMostRecentWeekday } from '../../../../utilities/dateUtilities';
import { hasNavReconIncomingNavs } from './pipelineMetadata';

const NAV_RECON_OVERVIEW_PATH = generatePath(
  OverviewsRoutePaths.NAV_RECON_OVERVIEW,
  { section: 'nav-recon' },
);

/**
 * Polls today's pipeline metadata and returns whether the NAV-Recon top-nav
 * badge should show (at least one fund has calculation complete and is not
 * approved). Toasts once when true, except while already on the overview page.
 *
 * Only runs when the user has the NAV_RECONCILIATION resource.
 */
export const useNavReconIncomingNavBadge = (enabled: boolean): boolean => {
  const navigate = useNavigate();
  const isOnLinkedPage = Boolean(
    useMatch({ path: NAV_RECON_OVERVIEW_PATH, end: true }),
  );

  const { data: userResources } = useUserResources(
    TokenService.getUser()?.user_id,
  );
  const hasNavReconResource = hasUserResource(
    userResources,
    NAV_RECONCILIATION_RESOURCE_NAME,
  );
  const isEnabled = enabled && hasNavReconResource;

  const mostRecentWeekDate = isEnabled ? getMostRecentWeekday() : undefined;

  const pipeline = useNavReconPipelineMetadata(mostRecentWeekDate);

  const hasIncomingNavs = useMemo(
    () =>
      isEnabled && hasNavReconIncomingNavs(pipeline.data, mostRecentWeekDate),
    [isEnabled, pipeline.data, mostRecentWeekDate],
  );

  const hasToastedRef = useRef(false);

  useEffect(() => {
    if (!hasIncomingNavs || hasToastedRef.current || isOnLinkedPage) return;

    hasToastedRef.current = true;
    toast({
      title: 'NAV Recon',
      message: `New NAV Reconciliation of ${dayjs(mostRecentWeekDate).format('DD/MM/YYYY')} require action`,
      type: 'link',
      duration: 10000,
      onClick: () => navigate(NAV_RECON_OVERVIEW_PATH),
    });
  }, [hasIncomingNavs, isOnLinkedPage, navigate, mostRecentWeekDate]);

  return hasIncomingNavs;
};
